import { Node } from "@tiptap/core";

// Blocos de layout dos e-mails em HTML: as faixas e caixas dos templates de
// produção (consignado-trabalhador, emprestimo-pago-email-*.template.ts), com
// o mesmo estilo inline — é o que os clientes de e-mail respeitam. No editor,
// o bloco envolve parágrafos, títulos e listas comuns.

/** Recuo lateral da moldura; as faixas o compensam para ocupar a largura toda. */
const RECUO = "32px";

export type EstiloBloco = "faixa" | "dados" | "atencao" | "alerta" | "suporte" | "rodape";

export const BLOCOS: { id: EstiloBloco; nome: string; css: string }[] = [
  {
    id: "faixa",
    nome: "Cabeçalho (faixa verde)",
    css: `background-color: #5B9B7F; color: #ffffff; padding: 32px 30px; text-align: center; margin: 0 -${RECUO} 24px;`,
  },
  {
    id: "dados",
    nome: "Caixa de dados",
    css: "background-color: #f8fcff; border-left: 4px solid #7BC77F; padding: 12px 20px; margin: 20px 0;",
  },
  {
    id: "atencao",
    nome: "Atenção (amarela)",
    css: "background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 12px 20px; margin: 25px 0;",
  },
  {
    id: "alerta",
    nome: "Alerta (vermelha)",
    css: "background-color: #fff5f5; border-left: 4px solid #dc3545; padding: 12px 20px; margin: 25px 0;",
  },
  {
    id: "suporte",
    nome: "Suporte (verde clara)",
    css: "background-color: #e8f4f0; border: 1px solid #7BC77F; padding: 16px 20px; margin: 25px 0; text-align: center;",
  },
  {
    id: "rodape",
    nome: "Rodapé (faixa verde)",
    css: `background-color: #5B9B7F; color: #ffffff; padding: 20px 30px; text-align: center; font-size: 12px; margin: 24px -${RECUO} 0;`,
  },
];

const cssDoBloco = (estilo: string): string =>
  BLOCOS.find((b) => b.id === estilo)?.css ?? BLOCOS[1].css;

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    blocoEmail: {
      /** Envolve o trecho selecionado no bloco; se já estiver num, troca o estilo. */
      setBlocoEmail: (estilo: EstiloBloco) => ReturnType;
      /** Tira o trecho do bloco, devolvendo os parágrafos ao corpo. */
      unsetBlocoEmail: () => ReturnType;
    };
  }
}

export const BlocoEmail = Node.create({
  name: "blocoEmail",
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      estilo: {
        default: "dados",
        parseHTML: (el) => el.getAttribute("data-bloco") ?? "dados",
        renderHTML: (attrs) => ({ "data-bloco": attrs.estilo, style: cssDoBloco(attrs.estilo) }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-bloco]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", HTMLAttributes, 0];
  },

  addCommands() {
    return {
      setBlocoEmail:
        (estilo) =>
        ({ commands, editor }) =>
          editor.isActive(this.name)
            ? commands.updateAttributes(this.name, { estilo })
            : commands.wrapIn(this.name, { estilo }),
      unsetBlocoEmail:
        () =>
        ({ commands }) =>
          commands.lift(this.name),
    };
  },
});

// Moldura: a coluna branca de 650px com borda dos e-mails de produção. Entra
// ao salvar o corpo em HTML e sai ao carregar no editor, que a mostra pela
// própria área de edição — assim o editor não precisa representá-la.
export const MOLDURA_CSS = `max-width: 650px; margin: 0 auto; background-color: #ffffff; border: 1px solid #e0e0e0; padding: 0 ${RECUO}; font-family: Arial, Helvetica, sans-serif; font-size: 14px; line-height: 1.6; color: #333333;`;

const ABRE_MOLDURA = `<div data-moldura="granatech" style="${MOLDURA_CSS}">`;
const MOLDURA_REGEX = /^\s*<div data-moldura="granatech"[^>]*>([\s\S]*)<\/div>\s*$/;

// O editor sempre deixa um parágrafo vazio depois do último bloco (para dar
// onde digitar); no e-mail ele vira uma faixa branca embaixo do rodapé.
const PARAGRAFOS_VAZIOS_NO_FIM = /(\s*<p[^>]*>\s*(<br[^>]*>)?\s*<\/p>)+\s*$/;

export const comMoldura = (html: string): string => {
  if (!html.trim() || MOLDURA_REGEX.test(html)) return html;
  return `${ABRE_MOLDURA}${html.replace(PARAGRAFOS_VAZIOS_NO_FIM, "")}</div>`;
};

export const semMoldura = (html: string): string => html.match(MOLDURA_REGEX)?.[1] ?? html;
